import { Component, Input, OnDestroy, ViewChild } from '@angular/core';

import {
    ControlContainer,
    FormArray,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { InvalidFieldComponent } from '../../../../shared/components/invalid-field/invalid-field.component';
import { SearchFormComponent } from '../../../../shared/components/search-form/search-form.component';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { ShipmentFormComponent } from '../../../../shared/components/shipment-form/shipment-form.component';
import { FacilityInformation, FeaturesConfig, OperatingHour } from '../../../../shared/models/organization-data.model';
import { FORM_SECTION_DESCRIPTIONS } from '../../const/form-help-text.const';
import {
    AppFeaturesForm,
    AppFeaturesShipmentOrSearchForm,
    AppForm,
    FooterSectionFormControls,
    OperatingHourFormControls,
} from '../../models/app-form';
import { FacilityInformationComponent } from '../facility-information-component/facility-information-component.component';
import { FooterSectionComponent } from '../footer-section-component/footer-section-component.component';
import { FeatureSectionFormErrorJSON } from './models/feature-section';
import { formFeatureSectionErrors } from './utils/form-errors';

@Component({
    selector: 'app-feature-section',
    imports: [
        ReactiveFormsModule,
        MatExpansionModule,
        MatInputModule,
        MatRadioModule,
        MatSlideToggleModule,
        SearchFormComponent,
        ShipmentFormComponent,
        InvalidFieldComponent,
        FooterSectionComponent,
        FacilityInformationComponent,
        SectionHeaderComponent,
    ],
    templateUrl: './feature-section.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class FeatureSectionComponent implements OnDestroy {
    public featureSectionErrors: FeatureSectionFormErrorJSON;
    public otherFeatures: FeaturesConfig | undefined;
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.features;
    public readonly landingPageSettingsDescription = FORM_SECTION_DESCRIPTIONS.landingPageSettings;
    private unsubscribe$ = new Subject<void>();

    @Input() isEdition: boolean = false;
    @ViewChild(FacilityInformationComponent) facilityInformationComponent?: FacilityInformationComponent;

    @ViewChild(FooterSectionComponent) footerSectionComponent?: FooterSectionComponent;

    public setFacilityInformation(facilities: Partial<FacilityInformation>[] | undefined): void {
        if (!this.facilityInformationComponent) return;
        const arr = this.facilityInformationComponent.facilitiesArray;
        while (arr.length) arr.removeAt(0);
        (facilities ?? []).forEach((f) => {
            const fg = this.facilityInformationComponent?.createFacilityGroup();
            if (fg) {
                fg.patchValue({
                    airportName: f?.airportName ?? null,
                    airportCode: f?.airportCode ?? null,
                    address1: f?.address1 ?? null,
                    address2: f?.address2 ?? null,
                    phone: f?.phone ?? null,
                    firmsCode: f?.firmsCode ?? null,
                    externalFirmsCode: f?.externalFirmsCode ?? null,
                    position: { lat: f?.position?.lat ?? null, lng: f?.position?.lng ?? null },
                    city: f?.city ?? null,
                    state: f?.state ?? null,
                    zip: f?.zip ?? null,
                    country: f?.country ?? null,
                    amsNominationEmail: f?.amsNominationEmail ?? null,
                    workingDays: f?.workingDays ?? null,
                    openTime: f?.openTime ?? null,
                    closeTime: f?.closeTime ?? null,
                });
                const customers = fg.controls.customers;
                (f?.customers ?? []).forEach((c) => customers.push(new FormControl<string | null>(c ?? null)));
                const emails = fg.controls.email;
                (f?.email ?? []).forEach((e) => emails.push(new FormControl<string | null>(e ?? null)));
                const awb = fg.controls.awbPrefixes;
                (f?.awbPrefixes ?? []).forEach((p) => awb.push(new FormControl<string | null>(p ?? null)));
                const ops = fg.controls.operatingHours;
                (f?.operatingHours ?? []).forEach((oh: OperatingHour) =>
                    ops.push(
                        new FormGroup<OperatingHourFormControls>({
                            days: new FormControl(oh?.days ?? null),
                            hour: new FormControl(oh?.hour ?? null),
                        })
                    )
                );

                arr.push(fg);
            }
        });
    }

    public setFooterSections(
        sections: { title?: string; elements?: { type?: string; message?: string }[] }[] | undefined
    ): void {
        if (!this.footerSectionComponent) return;
        const arr = this.footerSectionComponent.footerSectionsArray;
        while (arr?.length) arr.removeAt(0);
        (sections ?? []).forEach((sec) => {
            const elements = (sec?.elements ?? []).map(
                (element) =>
                    new FormGroup({
                        type: new FormControl(element?.type ?? 'text', { nonNullable: true }),
                        message: new FormControl(element?.message ?? '', { nonNullable: true }),
                    })
            );
            const elementsArray = new FormArray(elements);
            const sectionGroup = new FormGroup<FooterSectionFormControls>({
                title: new FormControl(sec?.title ?? '', { nonNullable: true }),
                elements: elementsArray,
            });
            arr?.push(sectionGroup);
        });
    }

    public addFacility(): void {
        this.facilityInformationComponent?.addFacility();
    }

    public removeFacility(index: number): void {
        this.facilityInformationComponent?.removeFacility(index);
    }

    constructor(private parent: FormGroupDirective) {
        this.featureSectionErrors = formFeatureSectionErrors;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    get featuresFormGroup(): FormGroup<AppFeaturesForm> {
        return this.parentForm?.controls?.features;
    }

    get footerLogoWidthControl(): FormControl<string | null> {
        return this.featuresFormGroup?.controls?.footerLogoWidth;
    }

    get footerLogoHeightControl(): FormControl<string | null> {
        return this.featuresFormGroup?.controls?.footerLogoHeight;
    }

    get typeFlow(): FormControl<string | null> {
        return this.featuresFormGroup?.controls?.typeFlow;
    }

    get shipmentFormGroup(): FormGroup<AppFeaturesShipmentOrSearchForm> {
        return this.featuresFormGroup?.controls?.shipmentForm;
    }

    get searchFormGroup(): FormGroup<AppFeaturesShipmentOrSearchForm> {
        return this.featuresFormGroup?.controls?.searchForm;
    }

    get backgroundImagesArray():
        | FormArray<
              FormGroup<{
                  name: FormControl<string | null>;
              }>
          >
        | undefined {
        return this.searchFormGroup?.controls?.backgroundImages;
    }

    updateOtherFeatures(features: FeaturesConfig | undefined): void {
        this.otherFeatures = features || {};
    }

    public getFacilityInformationPayload(): FacilityInformation[] | null {
        return this.facilityInformationComponent?.getFacilityInformationPayload() ?? null;
    }
}
